import React, { useEffect, useState } from 'react';
import { config } from '../config/environment';
import { useFeatureFlag } from '../context/FeatureFlagContext';

interface VerifyStatus {
  is_email_verified: boolean;
  is_phone_verified: boolean;
  has_phone: boolean;
  must_verify: boolean;
}

const VerifyAccountPage: React.FC = () => {
  const enablePhoneVerification = useFeatureFlag('enable_phone_verification');
  const [status, setStatus] = useState<VerifyStatus | null>(null);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentMsg, setSentMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('authToken');
  const userType = localStorage.getItem('userType');
  const destination = userType === 'client' ? '/dashboard/client' : '/dashboard/coach';

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  function clearMustVerify() {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.must_verify = false;
      localStorage.setItem('userData', JSON.stringify(userData));
    } catch {
      // non-fatal
    }
  }

  useEffect(() => {
    if (!token) {
      window.location.href = '/';
      return;
    }
    fetch(`${config.apiBaseUrl}/verify/status`, { headers: authHeaders })
      .then(r => r.json())
      .then((data: VerifyStatus) => {
        setStatus(data);
        // Redirect only when there is nothing left to verify
        const canStillVerifyEmail = !data.is_email_verified;
        const canStillVerifyPhone = data.has_phone && !data.is_phone_verified;
        if (!canStillVerifyEmail && !canStillVerifyPhone) {
          clearMustVerify();
          window.location.href = destination;
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSendFor(ch: 'email' | 'sms') {
    setChannel(ch);
    setSending(true);
    setError(null);
    setSentMsg(null);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/send`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ channel: ch }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to send code');
      setCodeSent(true);
      setSentMsg(`Code sent to your ${ch === 'email' ? 'email address' : 'phone'}.`);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  async function handleVerify() {
    setVerifying(true);
    setError(null);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/check`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ channel, code }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Verification failed');
      clearMustVerify();
      window.location.href = destination;
    } catch (e: any) {
      setError(e.message);
    } finally {
      setVerifying(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-canvas flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-dark-surface rounded-xl border border-dark-border p-8 space-y-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-100">Verify your account</h1>
          <p className="text-sm text-dark-muted mt-1">
            Verify your email{enablePhoneVerification ? ' or phone number' : ''} to continue.
          </p>
        </div>

        {error && (
          <div className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{error}</div>
        )}
        {sentMsg && (
          <div className="text-xs text-green-400 bg-green-900/20 rounded-lg px-3 py-2">{sentMsg}</div>
        )}

        {!codeSent ? (
          <div className="space-y-3">
            {!status?.is_email_verified && (
              <button
                onClick={() => handleSendFor('email')}
                disabled={sending}
                className="w-full text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition-colors"
              >
                {sending && channel === 'email' ? 'Sending…' : 'Verify Email'}
              </button>
            )}
            {enablePhoneVerification && status?.has_phone && !status?.is_phone_verified && (
              <button
                onClick={() => handleSendFor('sms')}
                disabled={sending}
                className="w-full text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition-colors"
              >
                {sending && channel === 'sms' ? 'Sending…' : 'Verify Phone'}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-dark-muted">
              Enter 6-digit code from your {channel === 'email' ? 'email' : 'SMS'}
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="······"
              className="w-full text-sm bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-gray-200 placeholder-dark-muted focus:outline-none focus:border-violet-500 tracking-widest text-center"
            />
            <button
              onClick={handleVerify}
              disabled={verifying || code.length !== 6}
              className="w-full text-sm bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition-colors"
            >
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
            <button
              onClick={() => { setCodeSent(false); setCode(''); setSentMsg(null); }}
              className="w-full text-xs text-dark-muted hover:text-gray-300 transition-colors"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyAccountPage;
